// src/pages/fleet/TripTimeline.tsx — what happened on this trip, in order.
//
// The detail page already held every one of these facts, scattered across a
// metrics grid, two log tables and the map legend. Reading them meant knowing
// which panel each lived in and reassembling the order by hand. This is the
// same data on one line, in the order it happened.
//
// Nothing here is computed. Every step is a stored fact — a timestamp, a count,
// a measured distance — and a step whose fact is missing is not drawn. That is
// the point: an empty stretch of timeline means nothing happened, not that the
// page failed to work it out.
import React from 'react';
import type { TripDetails } from '../../services/fleetApi';
import Icon, { IconName } from './Icon';
import { fmtDur } from './reports/reportKit';
import useIsMobile from './useIsMobile';

// The detail page's own palette, not the dashboard's — the two differ, and a
// timeline whose green is a shade off the map's trail looks like a bug.
const TONE = {
  done: '#34C759',
  warn: '#FF9500',
  bad: '#FF3B30',
  gap: '#AF52DE',
  wait: '#8E8E93',
};
type Tone = keyof typeof TONE;

interface Step {
  key: string;
  icon: IconName;
  label: string;
  /** The number that makes the step worth reading — a duration, a distance. */
  detail?: string;
  at?: string | null;
  /** A caveat. Only ever set when there is one. */
  foot?: string;
  footTone?: Tone;
  tone: Tone;
}

/** "20 Oct, 10:27 am" — the year is noise on a journey measured in hours. */
const stamp = (v: string | null | undefined): string | null => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}, `
    + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const oneDec = (v: number | null | undefined) =>
  v == null ? null : v.toLocaleString('en-IN', { maximumFractionDigits: 1 });

export function buildSteps({ trip, metrics, stops, deviations, ifmsLogs, ifms }: TripDetails): Step[] {
  const steps: Step[] = [];
  const cancelled = trip.status === 'CANCELLED';

  steps.push({ key: 'created', icon: 'list', label: 'Trip Created', at: trip.createdAt, tone: 'done' });

  if (trip.startedAt) {
    steps.push({
      key: 'started', icon: 'power', label: 'Trip Started', at: trip.startedAt, tone: 'done',
      detail: metrics.waitingMin ? `${fmtDur(metrics.waitingMin)} at plant` : undefined,
      // The trail starts mid-route on these, so every distance below is short by
      // however far the truck had already driven. Saying so here is the only
      // place an operator meets that fact in order.
      foot: trip.lateCreation
        ? `raised after departure${trip.lateCreationKm != null ? ` · ~${oneDec(trip.lateCreationKm)} km missed` : ''}`
        : undefined,
      footTone: 'warn',
    });
  } else {
    steps.push({
      key: 'started', icon: 'power', label: 'Trip Started', tone: 'wait',
      detail: cancelled ? 'never started' : 'not started yet',
    });
  }

  // The milestones above and below happen in a fixed order; these do not. A halt
  // can come before or after a deviation, so they are collected and sorted by
  // when they actually happened rather than by the order this code lists them.
  const events: Step[] = [];

  // Halts are amber wherever they appear in this app; the unauthorised count is
  // what turns a halt from a fact into a question, so it is called out on its own.
  if (stops.length) {
    const unauth = stops.filter((s) => s.unauthorized).length;
    events.push({
      key: 'halt', icon: 'clock', label: `Halt (${stops.length})`,
      detail: fmtDur(metrics.haltMin), at: stops[0].startAt, tone: 'warn',
      foot: unauth ? `${unauth} unauthorised` : undefined,
      footTone: 'bad',
    });
  }

  // How far the truck drove off the planned road — the sum of the stretches,
  // which is what the labels on the map add up to.
  //
  // This used to show maxDeviationKm, the farthest it ever strayed from the
  // road. Both are real and they are not the same: on one trip the truck drove
  // 22.5 km off route while never being more than 2.6 km from it. Under a
  // heading that reads "Off Route", beside map labels that say "10.8 km off
  // route" and mean distance driven, the smaller number looked like the same
  // measurement understated.
  if (deviations.length) {
    const far = oneDec(metrics.maxDeviationKm);
    events.push({
      key: 'offroute', icon: 'pin', label: 'Off Route',
      detail: `${oneDec(metrics.deviationKm)} km driven`, at: deviations[0].startAt, tone: 'bad',
      foot: `${deviations.length} stretch${deviations.length > 1 ? 'es' : ''}`
        + (far ? ` · farthest ${far} km from the road` : ''),
    });
  }

  events.sort((a, b) => Date.parse(a.at || '') - Date.parse(b.at || ''));

  // Last of the events, and deliberately so: the metrics carry how many gaps
  // there were and how long the worst one lasted, but not when any of them
  // began. Guessing a position for it would be the one invented fact here.
  if (metrics.gapCount) {
    events.push({
      key: 'gap', icon: 'antenna', label: `GPS Gap (${metrics.gapCount})`,
      detail: metrics.longestGapMin ? `longest ${fmtDur(metrics.longestGapMin)}` : undefined,
      tone: 'gap',
      // Only when there is a distance to report. "0 km drawn across" reads as
      // if nothing was missed, which is the opposite of what a gap means.
      foot: metrics.bridgedKm ? `${oneDec(metrics.bridgedKm)} km drawn across` : undefined,
    });
  }

  steps.push(...events);

  if (trip.arrivedAt) {
    // arrivalKm is set only when the circle had to be widened, so null means
    // either a verified dealer location or no dealer at all. Claiming "Verified"
    // for a trip that never had a dealer would be inventing the check.
    const widened = trip.arrivalKm != null;
    const verified = !widened && !!trip.dealerCode;
    steps.push({
      key: 'arrived', icon: 'pin', tone: 'done', at: trip.arrivedAt,
      label: widened ? 'Arrived (Approx)' : verified ? 'Arrived (Verified)' : 'Arrived',
      detail: widened ? `within ${oneDec(trip.arrivalKm)} km` : 'at the destination',
      foot: widened ? 'dealer located to district only' : undefined,
      footTone: 'warn',
    });
  }

  // Arriving and delivering are different facts and are routinely hours apart.
  // Keeping them as two steps is the whole reason this distinction exists.
  if (trip.deliveryConfirmedAt) {
    steps.push({
      key: 'delivered', icon: 'check', label: 'Delivery Confirmed', at: trip.deliveryConfirmedAt, tone: 'done',
      detail: trip.ackQuantityMt != null ? `${trip.ackQuantityMt} MT acknowledged` : undefined,
      foot: trip.deliveryConfirmedBy || undefined,
    });
  }

  if (cancelled) {
    steps.push({
      key: 'end', icon: 'close', label: 'Trip Cancelled', at: trip.cancelledAt, tone: 'bad',
      detail: trip.cancelReason || undefined,
    });
  } else if (trip.completedAt) {
    steps.push({
      key: 'end', icon: 'check', label: 'Trip Completed', at: trip.completedAt, tone: 'done',
      detail: metrics.elapsedMin != null ? fmtDur(metrics.elapsedMin) : undefined,
    });
  } else {
    steps.push({
      key: 'end', icon: 'check', label: 'Trip Completed', tone: 'wait',
      detail: trip.status === 'ACTIVE' ? 'in progress' : 'not started',
    });
  }

  // The last step, and the one the trip's badge turns on: a finished trip reads
  // CLOSED until IFMS has accepted it and COMPLETED after. A cancelled trip is
  // never reported, so it gets no step at all rather than a grey one that would
  // read as something still owed.
  if (!cancelled) {
    const state = ifms.state || (ifms.configured ? 'pending' : 'off');
    const lastOk = [...ifmsLogs].reverse().find((l) => l.status === 'SUCCESS');
    steps.push({
      key: 'ifms', icon: 'shield',
      label: state === 'sent' ? 'Reported to IFMS' : 'IFMS Reporting',
      at: state === 'sent' ? lastOk?.updatedAt : undefined,
      tone: state === 'sent' ? 'done' : state === 'attention' ? 'bad' : 'wait',
      detail: state === 'sent' ? undefined
        : state === 'attention' ? 'needs attention'
        : state === 'pending' ? 'queued'
        : 'not switched on yet',
    });
  }

  return steps;
}

export default function TripTimeline({ details }: { details: TripDetails }) {
  const isMobile = useIsMobile();
  const steps = buildSteps(details);

  return (
    <div style={S.panel}>
      <div style={S.head}>
        <span style={S.headIcon}><Icon name="map" size={15} stroke={2.1} /></span>
        <span style={S.title}>Trip Progress &amp; Events Timeline</span>
      </div>
      {/* Horizontal while it fits and scrolls when it does not; stacked on a
          phone, where a seven-step rail is unreadable at any width. */}
      <div style={isMobile ? S.railV : S.railH}>
        {steps.map((s, i) => {
          const colour = TONE[s.tone];
          const prev = i > 0 ? TONE[steps[i - 1].tone] : null;
          const next = i < steps.length - 1 ? TONE[steps[i + 1].tone] : null;
          const node = (
            <span style={{
              ...S.node,
              background: s.tone === 'wait' ? 'var(--fx-surface)' : colour,
              color: s.tone === 'wait' ? TONE.wait : '#fff',
              border: s.tone === 'wait' ? `2px dashed ${TONE.wait}` : `2px solid ${colour}`,
              boxShadow: s.tone === 'wait' ? 'none' : `0 0 0 4px ${colour}22`,
            }}>
              <Icon name={s.icon} size={16} stroke={2.2} />
            </span>
          );
          const text = (
            <div style={isMobile ? S.textV : S.textH}>
              <div style={{ ...S.label, color: s.tone === 'bad' ? TONE.bad : 'var(--fx-text)' }}>{s.label}</div>
              {s.detail && <div style={S.detail}>{s.detail}</div>}
              {stamp(s.at) && <div style={S.at}>{stamp(s.at)}</div>}
              {s.foot && <div style={{ ...S.foot, color: TONE[s.footTone || 'wait'] }}>{s.foot}</div>}
            </div>
          );

          if (isMobile) {
            return (
              <div key={s.key} style={S.rowV}>
                <div style={S.gutterV}>
                  {node}
                  {next && <span style={{ ...S.lineV, background: `linear-gradient(180deg, ${colour}, ${next})` }} />}
                </div>
                {text}
              </div>
            );
          }
          return (
            <div key={s.key} style={S.colH}>
              <div style={S.nodeRow}>
                {prev && <span style={{ ...S.lineH, left: 0, right: '50%', background: `linear-gradient(90deg, ${prev}, ${colour})` }} />}
                {next && <span style={{ ...S.lineH, left: '50%', right: 0, background: `linear-gradient(90deg, ${colour}, ${next})` }} />}
                {node}
              </div>
              {text}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  panel: { background: 'var(--fx-surface)', border: '1px solid var(--fx-border)', borderRadius: 14, boxShadow: 'var(--fx-shadow)', marginBottom: 16, overflow: 'hidden' },
  head: { display: 'flex', alignItems: 'center', gap: 9, padding: '14px 18px 2px' },
  headIcon: { color: 'var(--fx-accent)', display: 'flex' },
  title: { fontSize: 15, fontWeight: 700, color: 'var(--fx-text)' },

  railH: { display: 'flex', alignItems: 'flex-start', gap: 0, padding: '18px 18px 20px', overflowX: 'auto' },
  colH: { flex: '1 0 128px', minWidth: 128, textAlign: 'center' },
  nodeRow: { position: 'relative', height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  // Sits under the node, which is why the node carries no background-clip tricks.
  lineH: { position: 'absolute', top: '50%', height: 4, marginTop: -2, borderRadius: 2 },

  railV: { display: 'flex', flexDirection: 'column', padding: '14px 18px 18px' },
  rowV: { display: 'flex', alignItems: 'stretch', gap: 12 },
  gutterV: { display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 'none' },
  lineV: { width: 4, flex: 1, minHeight: 16, borderRadius: 2 },

  node: { position: 'relative', zIndex: 1, width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', boxSizing: 'border-box' },

  textH: { marginTop: 9, padding: '0 6px' },
  textV: { paddingBottom: 14, minWidth: 0 },
  label: { fontSize: 13, fontWeight: 700, lineHeight: 1.3 },
  detail: { fontSize: 12, color: 'var(--fx-text-2)', marginTop: 2, lineHeight: 1.35 },
  at: { fontSize: 11.5, color: 'var(--fx-text-3)', marginTop: 2, whiteSpace: 'nowrap' },
  foot: { fontSize: 11, fontWeight: 600, marginTop: 3, lineHeight: 1.3 },
};
